import React, { useState } from "react";
import { Mail } from "lucide-react";
import { toast } from "sonner";
import { emailToolResult, apiError } from "../lib/api";

/**
 * Lead capture on tool result screens. Email is MOCKED server-side — the
 * template is rendered and logged, never sent. Consent is unchecked by default
 * and stored with a timestamp, consistent with the DPDP consent model.
 */
export function EmailMyResult({ tool, summary = {} }) {
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await emailToolResult({ email, tool, result_summary: summary, consent_marketing: consent });
      setSent(true);
      toast.success(r.message);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <p className="ui mt-8 border border-verify/40 bg-verify/[0.05] p-4 text-sm text-verify" data-testid="email-result-sent">
        Sent. Your result is in your inbox, in writing, with nothing added.
      </p>
    );
  }

  return (
    <form onSubmit={submit} className="mt-8 border-t border-rule pt-6" data-testid="email-result-form">
      <p className="ui flex items-center gap-2 text-sm font-medium text-ink">
        <Mail className="h-4 w-4 text-gold" aria-hidden="true" /> Email me this result
      </p>
      <p className="ui mt-1 text-xs text-dim">
        Your exact result, in writing, so you can check it with anyone. Stored only if you ask for this.
      </p>
      <div className="mt-3 flex max-w-md gap-2">
        <input
          required
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          aria-label="Email address"
          className="field"
          data-testid="email-result-input"
        />
        <button type="submit" disabled={busy} className="btn btn-secondary shrink-0" data-testid="email-result-send">
          {busy ? "Sending…" : "Send my result"}
        </button>
      </div>
      <label htmlFor={`email-consent-${tool}`} className="mt-3 flex max-w-md cursor-pointer items-start gap-3">
        <input
          id={`email-consent-${tool}`}
          type="checkbox"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          className="mt-1 h-4 w-4 accent-[var(--c-gold)]"
          data-testid="email-result-consent"
        />
        <span className="ui text-xs leading-[1.6] text-dim">
          Also send me occasional guidance and new-stone alerts. Unsubscribe anytime.
        </span>
      </label>
    </form>
  );
}
